import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { money } from "@/lib/scoring";
import { Badge } from "./Badge";
import { CountUp } from "./CountUp";
import { displayName } from "@/data/nicknames";

const GooseEgg = () => (
  <span
    role="img"
    aria-label="Goose egg"
    title="Goose egg"
    data-testid="goose-egg"
    className="ml-2 inline-block h-3.5 w-2.5 rounded-[50%] bg-cypress-cream align-middle shadow-[0_1px_3px_rgba(0,0,0,0.5),inset_0_-1px_2px_rgba(0,0,0,0.2)]"
  />
);

const SortTh = ({ label, col, sortKey, sortAsc, onSort, last = false }) => (
  <th className={`py-2 ${last ? "" : "pr-3 "}text-right`}>
    {onSort ? (
      <button
        type="button"
        data-testid={`sort-${col}`}
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 uppercase tracking-[0.16em] transition-colors hover:text-cypress-cream/70"
      >
        {label}
        {sortKey === col &&
          (sortAsc ? <ChevronUp className="h-3 w-3 text-cypress-gold" /> : <ChevronDown className="h-3 w-3 text-cypress-gold" />)}
      </button>
    ) : (
      label
    )}
  </th>
);

export const ResultsTable = ({
  players,
  testIdPrefix = "results",
  badgesFor = null,
  onSelect = null,
  ytdColumns = false,
  gooseEggs = false,
  sortKey = "totalWon",
  sortAsc = false,
  onSort = null,
}) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[640px] border-collapse text-sm" data-testid={`${testIdPrefix}-table`}>
      <thead>
        <tr className="border-b border-cypress-gold/20 text-left text-[10px] font-bold uppercase tracking-[0.16em] text-cypress-cream/40">
          <th className="py-2 pr-2">Rank</th>
          <th className="py-2 pr-3">Player</th>
          {ytdColumns && <SortTh label="Rounds" col="rounds" sortKey={sortKey} sortAsc={sortAsc} onSort={onSort} />}
          <th className="py-2 pr-3 text-right">Dots</th>
          <th className="py-2 pr-3 text-right">Dot Winnings</th>
          <th className="py-2 pr-3 text-right">Par In $</th>
          <th className="py-2 pr-3 text-right">Rabbit $</th>
          <th className="py-2 pr-3 text-right">Squirrel $</th>
          {ytdColumns && <SortTh label="Per Round" col="perRound" sortKey={sortKey} sortAsc={sortAsc} onSort={onSort} />}
          {onSort ? (
            <SortTh label="Net" col="totalWon" sortKey={sortKey} sortAsc={sortAsc} onSort={onSort} last />
          ) : (
            <th className="py-2 text-right">Net</th>
          )}
        </tr>
      </thead>
      <tbody>
        {players.map((p, i) => {
          const badges = badgesFor ? badgesFor(p) : [];
          return (
            <tr
              key={p.name}
              data-testid={`${testIdPrefix}-row-${i + 1}`}
              onClick={onSelect ? () => onSelect(p.name) : undefined}
              className={`border-b border-white/5 ${onSelect ? "cursor-pointer transition-colors hover:bg-white/5" : ""} ${
                i < 3 ? "bg-cypress-gold/[0.07]" : ""
              }`}
            >
              <td className="py-2.5 pr-2 font-mono font-bold text-cypress-gold">{i + 1}</td>
              <td className="py-2.5 pr-3">
                <span className="font-semibold tracking-tight">{displayName(p.name)}</span>
                {gooseEggs && p.dots === 0 && <GooseEgg />}
                {p.rounds != null && (
                  <span className="block font-mono text-[10px] text-cypress-cream/40">
                    {p.rounds} {p.rounds === 1 ? "round" : "rounds"}
                  </span>
                )}
                {badges.length > 0 && (
                  <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
                    {badges.map((b) => (
                      <Badge key={b} type={b} />
                    ))}
                  </span>
                )}
              </td>
              {ytdColumns && <td className="py-2.5 pr-3 text-right font-mono tabular-nums">{p.rounds}</td>}
              <td className="py-2.5 pr-3 text-right font-mono tabular-nums">{p.dots}</td>
              <td className="py-2.5 pr-3 text-right font-mono tabular-nums">{money(p.dotWinnings)}</td>
              <td className="py-2.5 pr-3 text-right font-mono tabular-nums">{money(p.parInWinnings)}</td>
              <td className={`py-2.5 pr-3 text-right font-mono tabular-nums ${p.rabbitNet < 0 ? "text-red-400" : ""}`}>
                {money(p.rabbitNet)}
              </td>
              <td className={`py-2.5 pr-3 text-right font-mono tabular-nums ${p.squirrelNet < 0 ? "text-red-400" : ""}`}>
                {money(p.squirrelNet)}
              </td>
              {ytdColumns && (
                <td className="py-2.5 pr-3 text-right font-mono tabular-nums">{money(p.rounds ? p.totalWon / p.rounds : 0)}</td>
              )}
              <td className="py-2.5 text-right font-mono font-bold tabular-nums text-cypress-gold">
                <CountUp value={p.totalWon} format={(v) => money(v)} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// Mobile variant: stacked cards, no horizontal scrolling. Each card
// shows rank, name and NET; tapping expands the money breakdown.
export const ResultsCards = ({ players, testIdPrefix = "results", badgesFor = null, gooseEggs = false }) => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-cards`}>
      {players.map((p, i) => {
        const badges = badgesFor ? badgesFor(p) : [];
        const open = openIdx === i;
        return (
          <div
            key={p.name}
            className={`rounded-lg border transition-colors duration-300 ${
              i < 3 ? "border-cypress-gold/40 bg-cypress-gold/[0.07]" : "border-cypress-gold/15 bg-cypress-green/40"
            }`}
          >
            <button
              type="button"
              data-testid={`${testIdPrefix}-mcard-${i + 1}`}
              onClick={() => setOpenIdx(open ? null : i)}
              aria-expanded={open}
              className="flex w-full items-center gap-3 px-4 py-3 text-left"
            >
              <span className="w-7 font-mono text-lg font-bold text-cypress-gold">{i + 1}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold tracking-tight">
                  {displayName(p.name)}
                  {gooseEggs && p.dots === 0 && <GooseEgg />}
                </span>
                {p.rounds != null && (
                  <span className="block font-mono text-[10px] text-cypress-cream/40">
                    {p.rounds} {p.rounds === 1 ? "round" : "rounds"}
                  </span>
                )}
                {badges.length > 0 && (
                  <span className="mt-1 flex flex-wrap gap-1">
                    {badges.map((b) => (
                      <Badge key={b} type={b} />
                    ))}
                  </span>
                )}
              </span>
              <span className="font-mono font-bold tabular-nums text-cypress-gold">
                <CountUp value={p.totalWon} format={(v) => money(v)} />
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-cypress-gold/60 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
              />
            </button>
            {open && (
              <dl
                data-testid={`${testIdPrefix}-mcard-detail-${i + 1}`}
                className="grid grid-cols-2 gap-x-6 gap-y-1.5 border-t border-white/5 px-4 py-3 text-xs"
              >
                {[
                  ["Dots", String(p.dots)],
                  ["Dot Winnings", money(p.dotWinnings)],
                  ["Par In", money(p.parInWinnings)],
                  ["Rabbit", money(p.rabbitNet)],
                  ["Squirrel", money(p.squirrelNet)],
                ].map(([label, val]) => (
                  <div key={label} className="flex items-center justify-between gap-2">
                    <dt className="uppercase tracking-[0.14em] text-cypress-cream/40">{label}</dt>
                    <dd className={`font-mono tabular-nums ${val.startsWith("-") ? "text-red-400" : "text-cypress-cream/80"}`}>{val}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        );
      })}
    </div>
  );
};
